import { css } from "styled-components";
import styled from "styled-components";
import FlexBox from "../../../FlexBox/FlexBox";
import { responsiveMap } from "../../../Grid/_utils/responsiveObserve";
import { MobileOperatingSystem } from "../../../../Utils/getMobileOperatingSystem";

interface IStickyBackgroundProps {
  $os: MobileOperatingSystem;
}

const _LandingHighlighProductContainer = styled(FlexBox)`
  display: flex;
  flex-direction: column;
  height: 100%;

  @media ${responsiveMap.maxLg} {
    text-align: center;
  }

  /* top content */
  .highlight-product-umami-top-content {
    width: 100%;
    max-width: 423px;
    position: absolute;
    top: 0;
    left: 0;
    @media ${responsiveMap.maxLg} {
      max-width: 100%;
    }
    @media (max-height: 740px) and (${responsiveMap.maxLg}) {
      transform: scale(0.9);
      transform-origin: top;
    }
  }

  /* bottom content */
  .highlight-product-umami-bottom-content {
    justify-content: flex-end;
    padding-right: 106px;
    margin-top: auto;
    @media ${responsiveMap.maxLg} {
      padding-right: 0;
    }
    .composition-container {
      @media (max-height: 740px) and (${responsiveMap.maxLg}) {
        transform: scale(0.85);
        transform-origin: bottom;
      }
    }
    .price-container {
      @media (max-height: 740px) and (${responsiveMap.maxLg}) {
        transform: scale(0.85);
        transform-origin: top;
        margin-top: 0;
        margin-bottom: 8px;
      }
    }

    .composition-list {
      @media (max-height: 740px) and (${responsiveMap.maxLg}) {
        gap: 2px;
      }
    }
  }
  .highlight-product-umami-bottom-content-wrapper {
    @media ${responsiveMap.maxLg} {
      width: 100%;
      /* align-items: center; */
    }
  }
`;

export const _LandingHighlighProductCoverContainer = styled(FlexBox)<
  {
    $bg?: {
      desktop?: string;
      mobile?: string;
    };
  } & IStickyBackgroundProps
>(({ $bg, $os }) => {
  const { desktop: desktopBg, mobile: mobileBg } = $bg || {};
  return css`
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh; /* Fallback */
    height: -webkit-fill-available;
    height: ${$os === "iOS" ? "100vh" : "100dvh"};
    padding-top: 64px;
    background-image: url(${desktopBg});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 1;
    display: flex;
    justify-content: center;

    @media ${responsiveMap.maxLg} {
      background-image: url(${mobileBg || desktopBg});
      padding-bottom: 164px;
    }

    .umami-logo {
      @media ${responsiveMap.maxLg} {
        height: 20px;
      }
    }
  `;
});

const _LandingHighlighProductUmamiRoot = styled("section")<
  {
    $bg?: {
      desktop?: string;
      mobile?: string;
    };
  } & IStickyBackgroundProps
>(({ $bg, $os }) => {
  const { desktop: desktopBg, mobile: mobileBg } = $bg || {};
  return css`
    background-image: ${desktopBg === "none" ? "none" : `url(${desktopBg})`};
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    height: 100vh; /* Fallback */
    height: -webkit-fill-available;
    height: ${$os === "iOS" ? "100vh" : "100dvh"};
    width: 100%;

    @media ${responsiveMap.maxLg} {
      background-image: ${mobileBg === "none"
        ? "none"
        : `url(${mobileBg || desktopBg})`};
      height: calc(100vh - 64px);
    }
  `;
});

export const _LandingHighlighProductContentContainer = styled(
  FlexBox
)<IStickyBackgroundProps>(({ $os }) => {
  return css`
    position: relative;
    padding: 66px 46px 36px;
    height: 100vh; /* Fallback */
    height: -webkit-fill-available;
    height: ${$os === "iOS" ? "100dvh" : "100dvh"};
    padding-top: calc(66px + 64px);
    display: flex;
    flex-direction: column;
    @media ${responsiveMap.maxLg} {
      padding: 66px 32px 32px;
      padding-top: calc(32px);
      height: calc(100dvh - 64px);
    }

    @media (max-height: 740px) and (${responsiveMap.maxLg}) {
      padding: 66px 6px 16px;
      padding-top: calc(16px);
    }
  `;
});

export const _FakeContentContainer = styled(FlexBox)`
  height: 100vh; /* Fallback */
  height: -webkit-fill-available;
  height: 100dvh;
`;

export const _HighlightProductUmamiCoverContainerWrapper = styled(
  "div"
)<IStickyBackgroundProps>(({ $os }) => {
  return css`
    transform: translateY(-100%);

    max-height: 100vh;
    max-height: -webkit-fill-available;
    max-height: ${$os === "iOS" ? "100vh" : "100dvh"};

    @media ${responsiveMap.maxLg} {
      height: calc(100vh);
      transform: translateY(-100vh);
    }
  `;
});

export const _StickyBackground = styled("div")<IStickyBackgroundProps>(
  ({ $os }) => {
    return css`
      position: sticky;
      top: 0;
      overflow: hidden;
      height: 100vh; /* Fallback */
      height: -webkit-fill-available;
      height: ${$os === "iOS" ? "100vh" : "100dvh"};

      @media ${responsiveMap.maxLg} {
        height: calc(100vh - 64px);

        .highlight-product-umami-background {
          transition: transform 1s ease-in-out;
          transform: scale(1.5);
        }
        .highlight-product-umami-cover-container-wrapper {
          transform: translateY(0);
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
          transition: transform 0.6s ease-in-out;
        }

        .highlight-product-umami-container {
          transition: opacity 1s ease-in-out;
          opacity: 0;
        }

        &.up {
          .highlight-product-umami-cover-container-wrapper {
            transform: translateY(-100vh);
          }
          .highlight-product-umami-background {
            transform: scale(1);
          }
          .highlight-product-umami-container {
            opacity: 1;
          }
        }
      }
    `;
  }
);

export { _LandingHighlighProductUmamiRoot, _LandingHighlighProductContainer };
